import Button from '@/components/ui/Button';
import { TrendingUp, Trophy, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

export default function RolePlayResults({ session, onNewSession }) {
  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No session data available</p>
      </div>
    );
  }

  const {
    discovery_score = 0,
    objection_handling_score = 0,
    closing_score = 0,
    overall_score = 0,
    techniques_used = [],
    conversation_history = [],
    difficulty_level,
    sales_stage,
    completed_at
  } = session;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Just now';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ScoreCard = ({ label, score, icon: Icon, description }) => {
    const getColor = (score) => {
      if (score >= 80) return 'bg-green-50 border-green-200';
      if (score >= 60) return 'bg-yellow-50 border-yellow-200';
      return 'bg-red-50 border-red-200';
    };

    const getScoreColor = (score) => {
      if (score >= 80) return 'text-green-700';
      if (score >= 60) return 'text-yellow-700';
      return 'text-red-700';
    };

    return (
      <div className={`p-4 rounded-lg border ${getColor(score)}`}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
        <p className={`text-3xl font-bold ${getScoreColor(score)}`}>
          {score}
        </p>
        <p className="text-xs text-gray-600 mt-2">{description}</p>
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
          <div
            className={`h-1.5 rounded-full transition-all ${
              score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-blue-600" />
            Session Complete
          </h3>
          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
            difficulty_level === 'beginner' ? 'bg-green-100 text-green-700' :
            difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
            difficulty_level === 'advanced' ? 'bg-orange-100 text-orange-700' :
            'bg-red-100 text-red-700'
          }`}>
            {difficulty_level?.charAt(0).toUpperCase() + difficulty_level?.slice(1)} • {sales_stage?.charAt(0).toUpperCase() + sales_stage?.slice(1)}
          </span>
        </div>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Completed {formatDate(completed_at)}
        </p>
      </div>

      {/* Overall Score */}
      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">Overall Performance</p>
          <TrendingUp className="w-5 h-5 text-blue-600" />
        </div>
        <p className="text-4xl font-bold text-blue-700">{overall_score}/100</p>
        <p className="text-xs text-gray-600 mt-2">
          {overall_score >= 80
            ? '🎉 Excellent! You executed the sales framework effectively'
            : overall_score >= 60
            ? '👍 Good effort! Keep practicing to refine your technique'
            : '🎯 Keep practicing! Focus on the techniques below'}
        </p>
      </div>

      {/* Score Breakdown */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Performance by Stage</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ScoreCard
            label="Discovery"
            score={discovery_score}
            icon={AlertTriangle}
            description="Needs analysis & qualification"
          />
          <ScoreCard
            label="Objection Handling"
            score={objection_handling_score}
            icon={AlertTriangle}
            description="Addressing concerns & resistance"
          />
          <ScoreCard
            label="Closing"
            score={closing_score}
            icon={CheckCircle2}
            description="Commitment & next steps"
          />
        </div>
      </div>

      {/* Techniques Used */}
      {techniques_used && techniques_used.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Techniques Demonstrated</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {techniques_used.map((technique, idx) => (
              <div key={idx} className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-medium text-blue-900">✓ {technique}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversation Summary */}
      {conversation_history && conversation_history.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Session Summary</h4>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-700 mb-3">
              <span className="font-semibold">{conversation_history.filter(m => m.role === 'user').length}</span> messages exchanged
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {conversation_history.slice(-4).map((msg, idx) => (
                <div key={idx} className="text-xs">
                  <p className={`font-medium ${msg.role === 'user' ? 'text-blue-700' : 'text-gray-700'}`}>
                    {msg.role === 'user' ? 'You:' : 'Prospect:'}
                  </p>
                  <p className="text-gray-600 ml-3">{msg.content.substring(0, 100)}...</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
        <h4 className="text-sm font-semibold text-indigo-900 mb-3">📈 Next Steps</h4>
        <ul className="text-sm text-indigo-700 space-y-2">
          {overall_score < 60 && (
            <li>• Review the sales framework basics before your next practice session</li>
          )}
          {discovery_score < 70 && (
            <li>• Practice asking more discovery questions to understand prospect needs</li>
          )}
          {objection_handling_score < 70 && (
            <li>• Focus on objection handling techniques in your next session</li>
          )}
          {closing_score < 70 && (
            <li>• Work on trial closes and commitment language</li>
          )}
          <li>• Repeat this scenario at a higher difficulty level to challenge yourself</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button className="flex-1">
          View Full Conversation Transcript
        </Button>
        <Button 
          onClick={onNewSession}
          className="flex-1"
        >
          Start New Session
        </Button>
      </div>
    </div>
  );
}
